import { calendarApi, type CalendarDto } from '../../services/api';
import { onLocaleChange, t } from '../../services/i18n';
import { addDays, createLocalDate, esc, startOfWeek, toIsoDate, type CalendarSourceFilter, type CalendarViewMode } from './calendar-shared';
import './rc-calendar-view';
import { RcCalendarView } from './rc-calendar-view';

const CALENDAR_EVENTS = ['nav', 'view-change', 'filter-change'] as const;

export class RcCalendar extends HTMLElement {
  private data: CalendarDto | null = null;
  private viewMode: CalendarViewMode = 'week';
  private sourceFilter: CalendarSourceFilter = 'all';
  private anchorDate = createLocalDate(new Date());
  private isLoading = false;
  private errorMessage = '';
  private requestId = 0;
  private disposeLocaleChange?: () => void;

  async connectedCallback(): Promise<void> {
    CALENDAR_EVENTS.forEach((eventName) => this.addEventListener(`calendar-${eventName}`, this.handleCalendarEvent as EventListener));
    this.disposeLocaleChange = onLocaleChange(() => this.render());
    this.isLoading = true;
    this.render();
    await this.loadData();
  }

  disconnectedCallback(): void {
    this.disposeLocaleChange?.();
    CALENDAR_EVENTS.forEach((eventName) => this.removeEventListener(`calendar-${eventName}`, this.handleCalendarEvent as EventListener));
  }

  private getRange(): { start: Date; end: Date } {
    if (this.viewMode === 'day') {
      return { start: createLocalDate(this.anchorDate), end: createLocalDate(this.anchorDate) };
    }
    const start = startOfWeek(this.anchorDate);
    return { start, end: addDays(start, 6) };
  }

  private async loadData(): Promise<void> {
    const currentRequestId = ++this.requestId;
    const { start, end } = this.getRange();
    this.isLoading = true;
    this.errorMessage = '';
    this.render();

    try {
      const data = await calendarApi.getRange(toIsoDate(start), toIsoDate(end));
      if (currentRequestId !== this.requestId) {
        return;
      }
      this.data = data;
      this.errorMessage = '';
    } catch (error: any) {
      if (currentRequestId !== this.requestId) {
        return;
      }
      this.errorMessage = `${t('calendar.loadError')}: ${esc(error.message)}`;
      if (!this.data) {
        this.innerHTML = `<div class="empty-state card">${this.errorMessage}</div>`;
      }
    } finally {
      if (currentRequestId !== this.requestId) {
        return;
      }
      this.isLoading = false;
      this.render();
    }
  }

  private render(): void {
    if (!this.data) {
      if (this.errorMessage) {
        this.innerHTML = `<div class="empty-state card">${this.errorMessage}</div>`;
        return;
      }
      this.innerHTML = `<div class="loading">${t('common.loading')}</div>`;
      return;
    }
    let view = this.querySelector('rc-calendar-view') as RcCalendarView | null;
    if (!view) {
      this.innerHTML = '<rc-calendar-view></rc-calendar-view>';
      view = this.querySelector('rc-calendar-view') as RcCalendarView | null;
    }
    if (!view) {
      return;
    }
    view.data = this.data;
    view.viewMode = this.viewMode;
    view.sourceFilter = this.sourceFilter;
    view.loading = this.isLoading;
    view.errorMessage = this.errorMessage;
    view.render();
  }

  private handleCalendarEvent = async (event: Event): Promise<void> => {
    const detail = (event as CustomEvent<any>).detail ?? {};
    switch ((event as CustomEvent).type) {
      case 'calendar-nav':
        if (detail.direction === 'prev') {
          this.anchorDate = addDays(this.anchorDate, this.viewMode === 'week' ? -7 : -1);
        } else if (detail.direction === 'next') {
          this.anchorDate = addDays(this.anchorDate, this.viewMode === 'week' ? 7 : 1);
        } else {
          this.anchorDate = createLocalDate(new Date());
        }
        await this.loadData();
        return;
      case 'calendar-view-change':
        if (detail.viewMode === this.viewMode) return;
        this.viewMode = detail.viewMode as CalendarViewMode;
        await this.loadData();
        return;
      case 'calendar-filter-change':
        this.sourceFilter = detail.sourceFilter as CalendarSourceFilter;
        this.render();
        return;
      default:
        return;
    }
  };
}

if (!customElements.get('rc-calendar')) {
  customElements.define('rc-calendar', RcCalendar);
}
